import { endpoints } from '@gopinhos/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../lib/api-client';

export interface LeadDocumentView {
  id: string;
  leadId: string;
  filename: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  virusScanStatus: 'pending' | 'clean' | 'infected' | 'failed';
  finalizedAt: string | null;
  createdAt: string;
  uploader: { id: string; fullName: string };
}

export function useLeadDocuments(leadId: string | undefined) {
  return useQuery({
    queryKey: ['leadDocuments', leadId],
    enabled: !!leadId,
    queryFn: () =>
      apiClient.request<unknown, never>({
        method: 'GET',
        path: endpoints.leadDocuments(leadId!),
      }) as Promise<{ items: LeadDocumentView[] }>,
  });
}

interface InitResp {
  documentId: string;
  uploadUrl: string;
  uploadHeaders: Record<string, string>;
  expiresAt: string;
}

/**
 * Two-step upload: hash the file (SHA-256), call /init for a signed URL,
 * PUT the bytes directly to R2, then call /finalize. Bytes never go through
 * the API — blueprint §6.8, §8.3.
 */
export function useUploadLeadDocument(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const buffer = await file.arrayBuffer();
      const digest = await crypto.subtle.digest('SHA-256', buffer);
      const checksum = Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');

      const init = (await apiClient.request({
        method: 'POST',
        path: endpoints.documentInit(leadId),
        body: {
          originalFilename: file.name,
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          checksumSha256: checksum,
        },
      })) as InitResp;

      const put = await fetch(init.uploadUrl, {
        method: 'PUT',
        headers: init.uploadHeaders,
        body: file,
      });
      if (!put.ok) throw new Error(`upload failed: ${put.status}`);

      await apiClient.request({
        method: 'POST',
        path: endpoints.documentFinalize(leadId),
        body: { documentId: init.documentId },
      });
      return { documentId: init.documentId };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leadDocuments', leadId] });
    },
  });
}
